/**
 * InlogView: inloggen op Mijn Kleda zonder wachtwoord (plan 2026-10-28,
 * golf 1).
 *
 *   /inloggen   e-mailadres invullen; altijd dezelfde bevestigingszin, ook
 *               als we het adres niet kennen (anders is dit een adressenzoeker)
 *   /inlog/:t   de link uit mail MK01. Openen doet niets; pas de klik op
 *               "Open Mijn Kleda" verbruikt de link, want mailscanners openen
 *               links vóór de klant dat doet. Daarna naar Mijn Kleda op het
 *               ordertoken van de laatste bestelling; de sessie zit in een
 *               httpOnly-cookie en maakt het niveau volledig.
 *
 * kleda.nl linkt naar /inloggen met utm_medium=site; dat gaat als `bron` mee
 * naar het aanvraag-event.
 *
 *   /email-bevestigen/:t  (golf 2) de link uit mail MK02 naar een nieuw
 *               e-mailadres. Zelfde regel: openen doet niets, de klik
 *               bevestigt. Geen inlog nodig; de link is het bewijs.
 */
import { useState, type FormEvent } from 'react'
import { KLEDA_CONTACT } from '@platform/kleda-content'
import { bevestigEmail, vraagInlogAan, wisselInlogIn } from '../api'
import { trackEmailBevestigd, trackMijnKledaIngelogd, trackMijnKledaInlogAangevraagd } from '../analytics'
import { Card, StatusHeader } from './UI'
import { PageLayout } from './Layout'

const KNOP = 'rounded-xl bg-ink px-md py-sm text-body-sm font-semibold text-white disabled:opacity-40'

/** /inloggen, /inlog/:t of /email-bevestigen/:t; null = geen inlogpad. */
export function detectInlogPad(): { soort: 'aanvragen' } | { soort: 'inwisselen'; t: string } | { soort: 'email-bevestigen'; t: string } | null {
  if (typeof window === 'undefined') return null
  const pad = window.location.pathname
  if (/^\/inloggen\/?$/.test(pad)) return { soort: 'aanvragen' }
  const e = pad.match(/^\/email-bevestigen\/([^/?#]+)/)
  if (e) return { soort: 'email-bevestigen', t: decodeURIComponent(e[1]) }
  const m = pad.match(/^\/inlog\/([^/?#]+)/)
  return m ? { soort: 'inwisselen', t: decodeURIComponent(m[1]) } : null
}

function bronUitUrl(): string {
  const p = new URLSearchParams(window.location.search)
  const medium = p.get('utm_medium')
  return medium ? medium.slice(0, 40) : 'inlog'
}

function Hulp() {
  return (
    <p className="mt-lg text-[13px] text-ink-faint">
      Lukt het niet? Bel <a href={`tel:${KLEDA_CONTACT.telefoonTel}`} className="underline">{KLEDA_CONTACT.telefoonWeergave}</a> of mail{' '}
      <a href={`mailto:${KLEDA_CONTACT.email}`} className="underline">{KLEDA_CONTACT.email}</a>.
    </p>
  )
}

function Aanvragen() {
  const [email, setEmail] = useState('')
  const [bezig, setBezig] = useState(false)
  const [melding, setMelding] = useState<string | null>(null)
  const [fout, setFout] = useState<string | null>(null)

  const verstuur = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setBezig(true)
    setFout(null)
    const bron = bronUitUrl()
    const r = await vraagInlogAan(email.trim(), bron)
    setBezig(false)
    if (r.success && r.data) {
      setMelding(r.data.melding)
      trackMijnKledaInlogAangevraagd(bron)
      return
    }
    setFout(r.error?.message || 'Dat lukte niet. Probeer het zo opnieuw.')
  }

  return (
    <>
      <StatusHeader
        title="Mijn Kleda"
        subtitle="Je kleden, je bestellingen en je facturen op één plek. Geen wachtwoord nodig: we mailen je een link."
      />
      <Card>
        {melding ? (
          <>
            <p className="text-body text-ink">{melding}</p>
            <p className="mt-sm text-body-sm text-ink-muted">Geen mail gezien? Kijk ook even bij je ongewenste mail. De link werkt een uur.</p>
            <button type="button" onClick={() => { setMelding(null); setEmail('') }} className="mt-md text-body-sm text-ink-muted underline">
              Ander e-mailadres
            </button>
          </>
        ) : (
          <form onSubmit={(e) => void verstuur(e)}>
            <label htmlFor="mk-email" className="block text-body-sm font-medium text-ink mb-xs">Je e-mailadres</label>
            <input
              id="mk-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@voorbeeld.nl"
              className="input w-full"
              disabled={bezig}
            />
            <p className="mt-xs text-[13px] text-ink-faint">Gebruik het adres waarop je mails van ons krijgt.</p>
            <button type="submit" disabled={bezig || !email.trim()} className={`mt-md ${KNOP}`}>
              {bezig ? 'Even geduld...' : 'Stuur mij een inloglink'}
            </button>
            {fout && <p className="mt-sm text-body-sm text-red-700">{fout}</p>}
          </form>
        )}
      </Card>
      <p className="mt-md">
        <a href="https://kleda.nl" className="text-body-sm text-ink-muted underline">Terug naar kleda.nl</a>
      </p>
      <Hulp />
    </>
  )
}

function Inwisselen({ t }: { t: string }) {
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)

  const open = async () => {
    setBezig(true)
    setFout(null)
    const r = await wisselInlogIn(t)
    if (r.success && r.data?.orderToken) {
      trackMijnKledaIngelogd()
      window.location.href = `/?token=${encodeURIComponent(r.data.orderToken)}&type=mijn-kleda`
      return
    }
    setBezig(false)
    setFout(r.error?.message || 'Inloggen lukte niet. Vraag een nieuwe link aan.')
  }

  return (
    <>
      <StatusHeader title="Mijn Kleda" subtitle="Je bent er bijna. Klik op de knop om in te loggen." />
      <Card>
        {fout ? (
          <>
            <p className="text-body text-ink">{fout}</p>
            <a href="/inloggen" className={`mt-md inline-block ${KNOP}`}>Nieuwe link aanvragen</a>
          </>
        ) : (
          <button type="button" disabled={bezig} onClick={() => void open()} className={KNOP}>
            {bezig ? 'Even geduld...' : 'Open Mijn Kleda'}
          </button>
        )}
      </Card>
      <Hulp />
    </>
  )
}

function EmailBevestigen({ t }: { t: string }) {
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)
  const [klaar, setKlaar] = useState<{ nieuwEmail: string; orderToken: string | null } | null>(null)

  const bevestig = async () => {
    setBezig(true)
    setFout(null)
    const r = await bevestigEmail(t)
    setBezig(false)
    if (r.success && r.data) {
      trackEmailBevestigd()
      setKlaar(r.data)
      return
    }
    setFout(r.error?.message || 'Bevestigen lukte niet. Probeer het zo opnieuw.')
  }

  return (
    <>
      <StatusHeader title="Nieuw e-mailadres" subtitle="Klik op de knop om te bevestigen dat dit adres van jou is." />
      <Card>
        {klaar ? (
          <>
            <p className="text-body text-ink">Geregeld. We mailen je voortaan op <span className="break-all">{klaar.nieuwEmail}</span>.</p>
            {klaar.orderToken && (
              <a href={`/?token=${encodeURIComponent(klaar.orderToken)}&type=mijn-kleda`} className={`mt-md inline-block ${KNOP}`}>Naar Mijn Kleda</a>
            )}
          </>
        ) : fout ? (
          <>
            <p className="text-body text-ink">{fout}</p>
            <button type="button" disabled={bezig} onClick={() => void bevestig()} className={`mt-md ${KNOP}`}>Probeer opnieuw</button>
          </>
        ) : (
          <button type="button" disabled={bezig} onClick={() => void bevestig()} className={KNOP}>
            {bezig ? 'Even geduld...' : 'Bevestig dit e-mailadres'}
          </button>
        )}
      </Card>
      <Hulp />
    </>
  )
}

export function InlogView({ pad }: { pad: NonNullable<ReturnType<typeof detectInlogPad>> }) {
  return (
    <PageLayout>
      {pad.soort === 'aanvragen' ? <Aanvragen /> : pad.soort === 'email-bevestigen' ? <EmailBevestigen t={pad.t} /> : <Inwisselen t={pad.t} />}
    </PageLayout>
  )
}
